// Shared rendering for the CUSTOM (player-drawn) collection. A custom design is
// a `grid × grid` array of CSS colors ("" = transparent); every cosmetic slot
// renders that same grid, just placed/scaled to fit its context.

/** Paint a pixel grid into the box (x, y, w, h). Empty cells stay transparent. */
export function drawCustomGrid(
  ctx: CanvasRenderingContext2D,
  cells: readonly string[],
  grid: number,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  if (grid <= 0) return;
  const cw = w / grid;
  const ch = h / grid;
  for (let row = 0; row < grid; row += 1) {
    for (let col = 0; col < grid; col += 1) {
      const color = cells[row * grid + col];
      if (!color) continue;
      ctx.fillStyle = color;
      // Overdraw by ~0.5px so adjacent cells leave no seams when scaled.
      ctx.fillRect(x + col * cw, y + row * ch, cw + 0.6, ch + 0.6);
    }
  }
}

/** A blank grid (all transparent). */
export function makeEmptyCells(grid: number): string[] {
  return new Array(grid * grid).fill("");
}

/**
 * The distinct non-empty colors in a grid, most-used first (capped at `max`).
 * Used to drive the animated frame border from the player's own colors. Empty
 * when the grid has no painted cells.
 */
export function customPalette(cells: readonly string[], max = 6): string[] {
  const counts = new Map<string, number>();
  for (const c of cells) {
    if (!c) continue;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, max).map((e) => e[0]);
}

/**
 * The most-used non-empty color in a grid, for slots that need a single
 * representative color (trail tint, frame stroke, banner base). Falls back to a
 * neutral gray when the grid is empty.
 */
export function dominantColor(cells: readonly string[]): string {
  const counts = new Map<string, number>();
  let best = "";
  let bestN = 0;
  for (const c of cells) {
    if (!c) continue;
    const n = (counts.get(c) ?? 0) + 1;
    counts.set(c, n);
    if (n > bestN) {
      bestN = n;
      best = c;
    }
  }
  return best || "#9e9e9e";
}
